/* eslint-disable */
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import SignaturePad from "@/components/SignaturePad";
import PhotoUpload from "@/components/PhotoUpload";
import { Car, CheckCircle2, AlertCircle, Loader2, FileDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = `${BACKEND_URL}/api/public`;

function money(n) {
  return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function PublicSign() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [signature, setSignature] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [agree, setAgree] = useState(false);
  const [licenseFront, setLicenseFront] = useState(null);
  const [licenseBack, setLicenseBack] = useState(null);

  useEffect(() => {
    axios
      .get(`${PUBLIC_API}/agreement/${token}`)
      .then((r) => {
        setData(r.data);
        // Prefill with any photos already on file so the customer sees them
        if (r.data?.customer?.license_photo) setLicenseFront(r.data.customer.license_photo);
        if (r.data?.customer?.license_photo_back) setLicenseBack(r.data.customer.license_photo_back);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Invalid or expired signing link."));
  }, [token]);

  const submit = async () => {
    if (!signature) return;
    setSubmitting(true);
    try {
      await axios.post(`${PUBLIC_API}/agreement/${token}/sign`, {
        customer_signature: signature,
        terms: data?.terms || "",
        license_photo: licenseFront && licenseFront.startsWith("data:image") ? licenseFront : undefined,
        license_photo_back: licenseBack && licenseBack.startsWith("data:image") ? licenseBack : undefined,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not submit signature. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="text-red-500 mx-auto mb-3" size={36} />
            <h1 className="font-heading text-xl font-semibold mb-1">Unable to load agreement</h1>
            <p className="text-sm text-gray-600">{error}</p>
            <p className="text-xs text-gray-500 mt-4">If you received this link via SMS and it's not working, please contact the rental company.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center">
        <Loader2 className="animate-spin text-gray-400" size={28} />
      </div>
    );
  }

  if (done || data.signed) {
    const pdfUrl = `${BACKEND_URL}/api/public/agreement/${token}/pdf`;
    return (
      <div className="min-h-screen bg-gray-50 grid place-items-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="text-green-500 mx-auto mb-3" size={48} />
            <h1 className="font-heading text-2xl font-bold mb-1">Thank you!</h1>
            <p className="text-sm text-gray-600 mb-2">Your rental agreement has been signed.</p>
            {data.signed_at && <p className="text-xs text-gray-500 mb-5">Signed {fmtDateTime(data.signed_at)}</p>}
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="public-download-pdf"
              className="inline-flex items-center justify-center gap-2 w-full bg-black hover:bg-gray-800 text-white font-medium text-sm rounded-md px-4 py-2.5 transition"
            >
              <FileDown size={16} /> Download signed PDF
            </a>
            <a
              href={`/renew/${token}`}
              data-testid="public-renew-rental"
              className="inline-flex items-center justify-center gap-2 w-full mt-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-900 font-medium text-sm rounded-md px-4 py-2.5 transition"
            >
              Renew rental
            </a>
            <p className="text-[11px] text-gray-500 mt-3">A copy has also been texted to you.</p>
            <p className="text-xs text-gray-500 mt-6">
              {data.business?.name} · {data.business?.phone}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const c = data.customer || {};
  const v = data.vehicle || {};
  const r = data.reservation || {};
  const b = data.business || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-black text-white grid place-items-center">
            <Car size={18} />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-base">{b.name || "Fair Rideshare"}</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-gray-500">Vehicle Rental Agreement</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 md:p-6 pb-32">
        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 mb-4">
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mb-1">Please sign your rental agreement</h1>
          <p className="text-sm text-gray-600">Review the details below, read the terms, and sign at the bottom.</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Renter</div>
              <div className="font-medium">{c.full_name}</div>
              {c.address && <div className="text-gray-600">{c.address}</div>}
              {c.phone && <div className="text-gray-600">{c.phone}</div>}
              {c.license_number && <div className="text-xs text-gray-500 mt-1">License: <span className="font-mono">{c.license_number}</span></div>}
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Vehicle</div>
              <div className="font-medium">{v.year} {v.make} {v.model}</div>
              <div className="text-gray-600">Plate: <span className="font-mono">{v.plate || "—"}</span></div>
              <div className="text-xs text-gray-500 mt-1">VIN: <span className="font-mono">{v.vin || "—"}</span></div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Pickup</div>
              <div className="font-medium">{fmtDateTime(r.pickup_datetime)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Return</div>
              <div className="font-medium">{fmtDateTime(r.return_datetime)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Rate</div>
              <div className="font-medium capitalize">{r.rate_type} @ {money(r.rate_amount)}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Weekly Outstanding</div>
              <div className="text-sm">Balance: <span className="font-semibold tabular-nums text-amber-700">{money(r.balance_due)}</span></div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 mb-4">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Terms &amp; Conditions</div>
          <div className="text-sm whitespace-pre-line leading-relaxed text-gray-800 max-h-[420px] overflow-y-auto pr-3 border border-gray-100 rounded-md p-3 bg-gray-50">
            {data.terms || "No terms provided."}
          </div>
          <div className="mt-3 text-[11px] text-gray-500">Template version: <span className="font-mono">2026-06-07</span></div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 mb-4" data-testid="public-license-section">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Driver's License</div>
          <p className="text-sm text-gray-600 mb-4">
            Please upload clear photos of the <strong>front</strong> and <strong>back</strong> of your driver's license. Optional — you can sign without it, but uploading helps us verify your reservation faster.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PhotoUpload
              testId="public-license-front"
              label="Front of license"
              hint="Tap to take or choose a photo"
              value={licenseFront}
              onChange={setLicenseFront}
              capture
            />
            <PhotoUpload
              testId="public-license-back"
              label="Back of license"
              hint="Tap to take or choose a photo"
              value={licenseBack}
              onChange={setLicenseBack}
              capture
            />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-5 md:p-6 mb-4">
          <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Your signature</div>
          <SignaturePad testId="public-signature" value={signature} onChange={setSignature} />
          <label className="flex items-start gap-2 mt-4 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
              className="mt-0.5"
              data-testid="public-agree-checkbox"
            />
            <span>
              I, <strong>{c.full_name || "Renter"}</strong>, have read and agree to the terms of this rental agreement. I understand my electronic signature is legally binding.
            </span>
          </label>
        </div>
      </main>

      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-20">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="text-xs text-gray-500 flex-1 hidden md:block">
            By signing, you accept the terms above.
          </div>
          <Button
            onClick={submit}
            disabled={!signature || !agree || submitting}
            className="bg-black hover:bg-gray-800 text-white flex-1 md:flex-none md:px-8"
            data-testid="public-submit"
          >
            {submitting ? (<><Loader2 size={14} className="mr-2 animate-spin" /> Submitting…</>) : "Sign & submit"}
          </Button>
        </div>
      </footer>
    </div>
  );
}
